import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import useWebSocket from 'react-use-websocket';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import { REPORT_EXPORTS_FIELD, REPORT_HTML_FIELD, REPORT_ID_FIELD, REPORT_PARAM_KEYS, REPORT_PARAMS_FIELD } from '../../constants/agentChat.constants';
import { sanitizeProseHtml } from '../../utils/helpers/sanitize.helpers';
import parse, { domToReact } from 'html-react-parser';
import { fileHelpers, systemHelpers } from '../../utils/helpers';
import { agentChatStepsHelpers } from '../../utils/helpers/agentChatSteps.helpers';
import {
  agentWebsocketActionsConstants,
  websocketActionsConstants,
} from '../../constants';

const defaultOptions = systemHelpers.getWebsocketOptions();

// Matches a full GFM-style markdown table block: a header row, its `|---|---|` separator, and data rows.
const MARKDOWN_TABLE_REGEX = /^\|.+\|[ \t]*\r?\n^\|(?:[ \t]*:?-+:?[ \t]*\|)+[ \t]*\r?\n(?:^\|.*\|[ \t]*\r?\n?)*/m;

const parseMarkdownTable = (block) => {
  const splitRow = (line) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
  const [headerLine, , ...rowLines] = block.trim().split(/\r?\n/).filter(Boolean);
  return {
    headers: splitRow(headerLine),
    rows: rowLines.map(splitRow),
  };
};

/**
 * Owns the agent chat protocol: websocket connection, message/session state,
 * and send/upload handlers. Presentational components (drawer/embedded
 * AgentChat, the full-width chat, or any future layout) consume this hook
 * and own their own JSX/styling independently.
 */
export const useAgentChat = ({
  flow,
  open,
  query = {},
  skipInitialMessage = false,
  deferClearOnClose = false,
  welcomeText,
  onClose,
  onConnect,
  onUserMessage,
  onAssistantMessage,
}, ref) => {
  const [t] = useTranslation(['main', 'agentChat']);

  const [connect, setConnect] = useState(false);
  const [chatDetails, setChatDetails] = useState({
    started: false,
    session_id: null,
  });
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [input, setInput] = useState('');
  const [welcomeShown, setWelcomeShown] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [attachmentDrafts, setAttachmentDrafts] = useState([]);
  // What the agent last said it is doing while it works (see the message effect); null between answers.
  const [progress, setProgress] = useState(null);
  // The steps the agent has announced and not yet finished, newest last (see `agentChatStepsHelpers`).
  const openStepsRef = useRef([]);
  // Forgets the steps and the line they drew. Called wherever a turn begins or ends without a reply to clear them: a send
  // (a step frame of an abandoned request can arrive after its reply, and `forceNew` can start a chat while an older
  // one is still running), a socket error, and clearing the chat.
  const resetProgress = () => {
    openStepsRef.current = [];
    setProgress(null);
  };

  const {companyId, token} = systemHelpers.getAuthDetails();

  // A plain ref wouldn't re-run the scroll-observer effect below when the scrollable node itself
  // mounts (e.g. once `socketUrl` flips truthy and the body is first rendered) — a ref's target
  // changing doesn't trigger effects, only a dependency changing does. A callback ref backed by
  // state does.
  const [chatBodyNode, setChatBodyNode] = useState(null);
  const chatBodyRef = useCallback((node) => setChatBodyNode(node), []);
  const fileInputRef = useRef(null);
  const senderRef = useRef(null);
  const userMessageKeyRef = useRef(0);
  // The backend accepts one attachment per action, so staging several files means sending
  // them as chained follow-up turns: the first goes out with the typed message, then each
  // remaining draft is sent as soon as the previous one's response (and session_id) arrives.
  const attachmentSendQueueRef = useRef([]);
  // Set while a deferred clear (`deferClearOnClose`) is still waiting for the caller's close animation to end.
  const clearPendingRef = useRef(false);
  const wasOpenRef = useRef(false);

  const socketUrl = useMemo(() => {
    if (!companyId || !open) return null;
    let url = `${systemHelpers.getWebsocketUrl('agentChat')}/${companyId}/${flow}/`;
    // Asks the backend for `processing_step` frames; without it none are sent. `URLSearchParams` joins onto a URL
    // that has no query of its own yet, which is every URL built here.
    const params = new URLSearchParams(agentChatStepsHelpers.withProcessingStepEvents(query)).toString();
    return `${url}?${params}`;
  }, [companyId, flow, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isInputDisabled = useMemo(() => {
    const latestMessage = messages[messages.length - 1];
    const isOptionsType = latestMessage && (['options', 'confirmation'].includes(latestMessage.type) || latestMessage?.options) && latestMessage?.options?.length > 0;
    return loading || isOptionsType;
  }, [loading, messages]);

  // Helper to create message objects
  const createMessage = ({ type = 'text', message = '', options, role = 'assistant', file, ...rest }) => ({
    id: `${Date.now()}-${type}-${Math.random()}`,
    role,
    type,
    message,
    options,
    file,
    ...rest
  });

  // When the chat opens, add the welcomeText as the first assistant message (once)
  useEffect(() => {
    if (open && welcomeText && !welcomeShown) {
      // Only add string messages. If welcomeText is not a string, convert to string as fallback.
      const text = typeof welcomeText === 'string' ? welcomeText : String(welcomeText);
      const welcomeMsg = createMessage({ message: text, role: 'assistant' });
      setMessages(prev => [welcomeMsg, ...prev]);
      setWelcomeShown(true);
    }
  }, [open, welcomeText, welcomeShown]);

  useEffect(() => {
    if (open && !connect) {
      setConnect(true);
    }
  }, [connect, open]);

  // Keeps the body pinned to the bottom for anything that changes its content height — a new
  // message, the waiting indicator appearing/disappearing, its progress text growing, a table or
  // report rendering inside a message — rather than re-scrolling only on the specific state
  // changes we remembered to list, which missed cases (H1MVP-4967).
  //
  // ⚠ ONLY WHILE THE READER IS ALREADY AT THE BOTTOM. The observer fires on every content change,
  // and the agent rewrites its progress line every few seconds while it works (WIT-261), so pinning
  // unconditionally dragged a reader who had scrolled up to re-read a long report back down on each
  // rewrite, and again when the answer landed — measured: parked at the top of a 40-row table, pulled
  // to the bottom twice before the answer was readable. `pinnedToBottomRef` follows the reader
  // instead: scrolling away switches the pinning off, scrolling back to the bottom switches it on.
  const pinnedToBottomRef = useRef(true);
  useEffect(() => {
    if (!chatBodyNode) return undefined;
    // Slack, because "at the bottom" is rarely an exact equality: sub-pixel layout, a rounded
    // scrollHeight, and a scrollTop the browser clamps its own way.
    const PIN_SLACK_PX = 24;
    const isAtBottom = () => chatBodyNode.scrollHeight - chatBodyNode.clientHeight - chatBodyNode.scrollTop <= PIN_SLACK_PX;
    const followTheReader = () => {
      pinnedToBottomRef.current = isAtBottom();
    };
    const scrollToBottom = () => {
      if (!pinnedToBottomRef.current) return;
      chatBodyNode.scrollTop = chatBodyNode.scrollHeight;
    };
    // A chat that has just opened starts on its newest message, whatever the reader did in the last one.
    pinnedToBottomRef.current = true;
    scrollToBottom();
    chatBodyNode.addEventListener('scroll', followTheReader, { passive: true });
    const observer = new MutationObserver(scrollToBottom);
    observer.observe(chatBodyNode, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      chatBodyNode.removeEventListener('scroll', followTheReader);
    };
  }, [chatBodyNode]);

  const { lastJsonMessage, readyState, sendJsonMessage } = useWebSocket(
    socketUrl,
    {
      ...defaultOptions,
      protocols: ['bearer', token],
      share: true,
      onOpen: () => onConnect?.(),
      onError: () => {
        resetProgress();
        setError('Connection error. Please try again.');
      },
    },
    connect && Boolean(socketUrl),
  );

  // Unconditional on purpose: it can run from a callback created on an earlier render (the drawer's
  // `afterOpenChange`), where "only if truthy" checks against that render's state would skip a reset.
  const clearState = () => {
    clearPendingRef.current = false;
    setChatDetails({ started: false, session_id: null });
    setError(null);
    setMessages([]);
    setInput('');
    setAttachmentDrafts([]);
    attachmentSendQueueRef.current = [];
    setLoading(false);
    resetProgress();
    userMessageKeyRef.current = 0;
    // Ensure welcome message can be shown again on next open
    setWelcomeShown(false);
  }

  const getActionName = (forceNew = false) => {
    return (!forceNew && chatDetails.started) ? agentWebsocketActionsConstants.CONTINUE_CHAT : agentWebsocketActionsConstants.NEW_CHAT;
  }

  useEffect(() => {
    if (!open && connect) {
      setConnect(false);
    }
  }, [open, readyState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // With `deferClearOnClose` the caller clears through `clearState` once its own close animation has
    // finished, so the conversation is not wiped while it is still on screen.
    if (open) wasOpenRef.current = true;
    // Only a chat that has actually been open has anything to clear: flagging it on mount would make the first
    // open wipe its own greeting.
    if (!open && deferClearOnClose && wasOpenRef.current) clearPendingRef.current = true;
    // Reopened before the close animation ended, so `afterOpenChange(false)` never fired: clear now.
    if (open && clearPendingRef.current) clearState();
    if (!open && !deferClearOnClose) {
      setTimeout(clearState, 300); // Delay to allow any final messages to be processed
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!lastJsonMessage || lastJsonMessage.action === websocketActionsConstants.PONG) return;

    // ⚠ A FRAME WITH AN `event` KEY IS NEVER AN ANSWER. It carries a `session_id` like one, so handled below it would
    // add an empty bubble, end the loading state and overwrite the session id. A `processing_step` frame only updates
    // the waiting line; any other `event` (one this build does not know yet) is dropped.
    if (agentChatStepsHelpers.isEventFrame(lastJsonMessage)) {
      if (!agentChatStepsHelpers.isProcessingStepFrame(lastJsonMessage)) return;
      // `openSteps` is captured here, not read back from the ref inside the updater: the updater runs when React next
      // renders, by which time a following frame may already have changed the ref (a `started` and its `done` close
      // together left the line empty, or on the previous label).
      const openSteps = agentChatStepsHelpers.applyStep(openStepsRef.current, lastJsonMessage);
      openStepsRef.current = openSteps;
      // Between two steps nothing is open, but the agent is still working: the last label stays until the next step
      // starts or the turn ends (the branch below), instead of flashing the default waiting text.
      setProgress((shown) => agentChatStepsHelpers.getCurrentLabel(openSteps, shown));
      return;
    }

    // ⚠ PROGRESS IS NOT AN ANSWER. While the agent works it streams one-line updates ("I'm retrieving expense data
    // from … to …") with no `session_id`, and every answer carries one. Handled as answers, each became a permanent
    // bubble, the first ended the loading state, and the session id was cleared until the answer landed, so a
    // message sent in between went out without one. It is the waiting line's text instead, until anything else arrives.
    if (!lastJsonMessage.session_id && typeof lastJsonMessage.message === 'string'
      && !lastJsonMessage.error && !lastJsonMessage.status && !lastJsonMessage.is_data_collected) {
      setProgress(lastJsonMessage.message);
      return;
    }
    // Anything else is the end of the turn (or the answer to it): the waiting line has nothing left to say.
    resetProgress();

    // Handle status-based responses
    if (lastJsonMessage.status === 'success') {
      onClose?.({confirmed: true, flow, query});
      return;
    }
    if (lastJsonMessage.status === 'error') {
      attachmentSendQueueRef.current = [];
      setError(lastJsonMessage.message);
      // Every other terminal branch below clears `loading`; leaving it set here disables the composer
      // and the full-width chat's New chat button for good, so an error makes the chat unusable until
      // the page is reloaded — with New chat the way out, that recovery must not be disabled.
      setLoading(false);
      return;
    }

    const {
      attachment, is_data_collected, error, message, session_id, options,
      content_type: contentType, [REPORT_HTML_FIELD]: reportHtml, [REPORT_EXPORTS_FIELD]: reportExports,
      [REPORT_ID_FIELD]: reportId, [REPORT_PARAMS_FIELD]: reportParams,
    } = lastJsonMessage || {};

    if (error) {
      attachmentSendQueueRef.current = [];
      setMessages(prev => ([
        ...prev,
        createMessage({
          type: 'error',
          message: t(`agentChat:errorCodes.${error}`),
        })
      ]));
      setLoading(false);
      return;
    }

    if (is_data_collected) {
      attachmentSendQueueRef.current = [];
      setLoading(false);
      onClose?.({confirmed: true});
      return;
    }

    if (!(skipInitialMessage && !chatDetails.started)) {
      const messageObject = createMessage({
        attachment, message, contentType, reportHtml, exports: reportExports, reportId, reportParams,
        ...(options ? { options, type: 'options' } : {}),
      });
      setMessages([...messages, messageObject]);
      onAssistantMessage?.({message, options, session_id});
    }

    if (chatDetails.session_id !== session_id) {
      setChatDetails({ started: true, session_id });
    }

    // Chain the next staged attachment onto this response's session_id rather than
    // chatDetails.session_id, which hasn't re-rendered with the value above yet.
    if (attachmentSendQueueRef.current.length > 0) {
      const [next, ...rest] = attachmentSendQueueRef.current;
      attachmentSendQueueRef.current = rest;
      sendQueuedAttachment(next, session_id);
    } else {
      setLoading(false);
    }
  }, [lastJsonMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  // `reportId` is a chip's report id (see `quickActionReportId`), or a report's own when it asks for itself again by
  // another angle (a pivot), with `reportParams` carrying that report's period and the new angle. Only the keys
  // `ReportRequestFields` reads are sent, and only as strings.
  const handleSend = (payload, {forceNew = false, reportId = null, reportParams = null} = {}) => {
    const text = typeof payload === 'string' ? payload : (payload?.text ?? payload?.message ?? '');
    if (!text) return;
    // Sending is the reader asking for the newest part of the conversation, so it re-pins the view
    // even if they had scrolled up: their own message appearing off-screen reads as nothing happening.
    pinnedToBottomRef.current = true;
    const messageKey = userMessageKeyRef.current;
    userMessageKeyRef.current += 1;
    onUserMessage?.({ message: text, messageKey });
    resetProgress();
    setLoading(true);
    setMessages(prev => ([...(forceNew ? [] : prev), createMessage({ message: text, role: 'user' })]));
    sendJsonMessage({
      action: getActionName(forceNew),
      body: {
        message: text,
        ...(reportId ? { [REPORT_ID_FIELD]: reportId } : {}),
        ...(reportId ? Object.fromEntries(REPORT_PARAM_KEYS
          .filter((key) => typeof reportParams?.[key] === 'string' && reportParams[key])
          .map((key) => [key, reportParams[key]])) : {}),
        ...(!forceNew && chatDetails.started && chatDetails.session_id ? { session_id: chatDetails.session_id } : {})
      }
    });
  };

  const handleSendAction = ({action, body = {}} = {}) => {
    if (!action) return;
    resetProgress();
    setLoading(true);
    sendJsonMessage({
      action,
      body: {
        ...body,
        ...(chatDetails.started && chatDetails.session_id ? { session_id: chatDetails.session_id } : {})
      }
    });
  };

  // Draws a stored conversation and carries on in its session: the next message goes out as `continue_chat`.
  const restoreSession = ({sessionId, messages: restored}) => {
    if (!sessionId || !restored?.length) return;
    setMessages(restored);
    setChatDetails({started: true, session_id: sessionId});
  };

  // Whether anything is on screen right now, read through a ref so a callback created on an earlier render
  // (a history request that has just come back) sees the truth rather than the state it closed over.
  // A caller deciding whether to draw a stored conversation must ask this and not its own flag: an
  // attachment-only send starts a conversation without going through `onUserMessage`.
  const conversationRef = useRef(false);
  conversationRef.current = messages.length > 0 || chatDetails.started;
  const hasConversation = () => conversationRef.current;

  // Leaves the conversation for an empty chat: the next message goes out as `new_chat`.
  const startNewChat = () => {
    clearState();
    if (selectedOptions.length) setSelectedOptions([]);
  };

  useImperativeHandle(ref, () => ({
    sendMessage: handleSend,
    sendAction: handleSendAction,
  }));

  // Helper to focus the sender input
  const focusSender = () => {
    if (senderRef.current && senderRef.current.focus) senderRef.current.focus();
  };

  useEffect(() => {
    focusSender();
  }, [messages]);

  useEffect(() => {
    if (open) focusSender();
  }, [open]);

  const onSubmit = () => {
    const text = input.trim();
    if (!text && attachmentDrafts.length === 0) return;
    if (attachmentDrafts.length > 0) {
      sendAttachments(attachmentDrafts, text);
      setAttachmentDrafts([]);
    } else {
      handleSend(text);
    }
    setInput('');
    focusSender();
  }

  // ⚠ SANITISE BETWEEN `marked` AND `parse`. `marked` emits whatever the model wrote — its own
  // `sanitize` option was removed years ago in favour of DOMPurify — and `html-react-parser` turns
  // that straight into elements. Measured before this line existed: `[x](javascript:alert(1))`
  // reached the DOM as a live anchor, and the hook below then added `target="_blank"` to it.
  const renderMarkdown = (value, toHtml) => parse(sanitizeProseHtml(toHtml(value || '')), {
    replace: (domNode) => {
      if (domNode && domNode.type === 'tag' && domNode.name === 'a') {
        const attribs = domNode.attribs || {};
        // Preserve existing attributes but enforce safe new-window behavior
        const props = {
          ...attribs,
          target: '_blank',
          rel: attribs.rel ? `${attribs.rel} noopener noreferrer` : 'noopener noreferrer',
        };
        return (
          <a {...props}>{domToReact(domNode.children || [])}</a>
        );
      }
      return undefined;
    },
  });
  const renderText = (value) => renderMarkdown(value, marked);
  // A table's header or cell is one line of inline markdown: `marked` would wrap it in a paragraph.
  const renderInline = (value) => renderMarkdown(value, marked.parseInline);

  // Returns { before, table, after } so callers can render a real table component in place of
  // the markdown table, instead of the raw <table> markup marked/html-react-parser would produce.
  // ⚠ THE CELLS ARE MARKDOWN TOO. Drawn as the plain strings `parseMarkdownTable` splits out, an answer's
  // `**TOTAL**` row showed its asterisks; each cell goes through the same sanitiser and link handling instead.
  const parseMessage = ({attachment, message, text} = {}) => {
    let content = text ?? message ?? '';
    if (attachment?.url && attachment?.filename && content) {
      // Agent messages may link to a file it generated via a placeholder URL (sandbox path,
      // temporary service link, etc.); swap that in for the real, downloadable attachment URL
      // whenever the linked filename matches the attachment's filename.
      content = content.replace(/\]\(([^)]*)\)/g, (match, url) => {
        const filename = url.split('/').pop().split(/[?#]/)[0];
        return filename === attachment.filename ? `](${attachment.url})` : match;
      });
    }

    const tableMatch = content.match(MARKDOWN_TABLE_REGEX);
    if (!tableMatch) {
      return { before: renderText(content), table: null, after: null };
    }

    const table = parseMarkdownTable(tableMatch[0]);
    return {
      before: renderText(content.slice(0, tableMatch.index).trim()),
      table: {headers: table.headers.map(renderInline), rows: table.rows.map((row) => row.map(renderInline))},
      after: renderText(content.slice(tableMatch.index + tableMatch[0].length).trim()),
    };
  };

  const handlePlusClick = () => fileInputRef.current?.click();

  // Sends an already-encoded attachment (base64 + filename). Shared by the file-input
  // change handler below and by callers that already have a pre-selected file to hand off
  // (e.g. an entry point that picks a file before the chat/socket even exists).
  const sendAttachment = ({base64, filename, size, type, message = '', forceNew = false} = {}) => {
    if (!base64 || !filename) return;
    resetProgress();
    setLoading(true);
    sendJsonMessage({
      action: getActionName(forceNew),
      body: {
        ...(!forceNew ? {session_id: chatDetails.session_id} : {}),
        message,
        attachment_base64: base64,
        attachment_filename: filename
      }
    });
    setMessages(prev => {
      const base = forceNew ? [] : prev;
      return [
        ...base,
        ...(message ? [createMessage({message, role: 'user'})] : []),
        {
          id: `${Date.now()}-${base.length}`,
          role: 'user',
          type: 'file',
          file: {name: filename, size, type},
        }
      ];
    });
  };

  // Sends a queued follow-up attachment against the session_id just returned by the
  // previous response — chatDetails.session_id can't be used here, it won't have re-rendered yet.
  const sendQueuedAttachment = (draft, sessionId) => {
    const {base64, filename, size, type} = draft;
    if (!base64 || !filename) return;
    sendJsonMessage({
      action: agentWebsocketActionsConstants.CONTINUE_CHAT,
      body: {session_id: sessionId, message: '', attachment_base64: base64, attachment_filename: filename}
    });
    setMessages(prev => ([
      ...prev,
      {id: `${Date.now()}-${prev.length}`, role: 'user', type: 'file', file: {name: filename, size, type}},
    ]));
  };

  // Sends staged drafts as one turn: the first carries the typed message, the rest queue up
  // as chained follow-ups (see the lastJsonMessage effect above).
  const sendAttachments = (drafts, message = '', {forceNew = false} = {}) => {
    const list = Array.isArray(drafts) ? drafts : [drafts];
    if (list.length === 0) return;
    const [first, ...rest] = list;
    attachmentSendQueueRef.current = rest;
    sendAttachment({...first, message, forceNew});
  };

  // Stages the file(s) in the composer instead of sending them — the user confirms via
  // onSubmit, optionally alongside a typed message.
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    try {
      const drafts = await Promise.all(files.map((file, index) => fileHelpers.buildAttachmentDraft(file, `${Date.now()}-${index}-${file.name}`)));
      setAttachmentDrafts(prev => [...prev, ...drafts]);
    } catch (err) {
      setError('Failed to upload file.');
    }
  };

  // Wired to the Attachments component's own "add more" tile/drop area, right next to the
  // staged previews — antd already assigns each selected file a uid, reused as our draft's.
  const handleAttachmentsChange = async ({file}) => {
    if (file.status === 'removed' || !file.originFileObj) return;
    if (attachmentDrafts.some((draft) => draft.uid === file.uid)) return;
    try {
      const draft = await fileHelpers.buildAttachmentDraft(file.originFileObj, file.uid);
      setAttachmentDrafts(prev => [...prev, draft]);
    } catch (err) {
      setError('Failed to upload file.');
    }
  };

  const removeAttachmentDraft = (item) => setAttachmentDrafts(prev => prev.filter((draft) => draft.uid !== item.uid));

  const toggleOption = (value) => {
    setSelectedOptions((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const submitOptions = (options) => {
    const text = options.join(', ');
    handleSend(text);
    setSelectedOptions([]);
  };

  return {
    socketUrl,
    readyState,
    messages,
    error,
    input,
    setInput,
    loading,
    progress,
    chatDetails,
    selectedOptions,
    isInputDisabled,
    chatBodyRef,
    fileInputRef,
    senderRef,
    handleSend,
    handleSendAction,
    restoreSession,
    hasConversation,
    startNewChat,
    clearState,
    sendAttachment,
    sendAttachments,
    attachmentDrafts,
    removeAttachmentDraft,
    handleFileChange,
    handleAttachmentsChange,
    handlePlusClick,
    toggleOption,
    submitOptions,
    onSubmit,
    parseMessage,
  };
};
